/**
 * Offline session (Phase 16A, design §6.3): enrollment, PIN unlock, lock and wipe.
 *
 * - Enroll (online, after a full sign-in): the clinician sets a 6-digit PIN; a DEK is created and
 *   wrapped with the PIN key; the user profile (permissions snapshot) and the Cognito refresh token
 *   are stored encrypted.
 * - Lock (idle timeout, manual, other tab): the DEK is dropped from memory.
 * - Unlock: the PIN unwraps the DEK. Five wrong PINs crypto-shred the store (escrow survives).
 * - Offline access is allowed for `offlineWindowHours` after the last successful online sign-in.
 *
 * A device can hold several enrolled users (shared agency tablet); each has its own database and
 * key. The plaintext registry lists them for the lock screen with a short label only.
 */
import type { AuthUser } from '../../types/permissions';
import { calibrateIterations, createWrappedDek, deriveKek, deriveKekFromSecret, identityHash, randomBytes, toBase64, fromBase64, unwrapDek, unwrapDekForRewrap, wrapDek } from './crypto';
import { PRF_HKDF_INFO } from './biometric';
import { SecureStore } from './secureStore';
import { indexedDbBackend, KvBackendFactory } from './platform/kvBackend';

export const MAX_PIN_ATTEMPTS = 5;
const REGISTRY_KEY = 'pelora-offline-identities';
const DB_PREFIX = 'pelora-offline-';

export interface OfflineSettings {
  offlineWindowHours: number;
  maxBackdateHours: number;
  packetWindowHours: number;
}

export const DEFAULT_OFFLINE_SETTINGS: OfflineSettings = { offlineWindowHours: 16, maxBackdateHours: 72, packetWindowHours: 36 };

/** Lock-screen entry. `label` is the clinician's first name and last initial; no patient data. */
export interface OfflineIdentity {
  id: string;
  label: string;
  lastUsedAt: string;
}

/** Encrypted profile of the enrolled user (store `profile`, key `user`). */
export interface OfflineProfile {
  user: AuthUser;
  deviceId: string;
  offlineSettings: OfflineSettings;
  enrolledAt: string;
  lastOnlineAuthAt: string;
}

interface KeyMeta { wrappedDek: string; salt: string; iterations: number }
/** 16D D2: the DEK wrapped a second time with a WebAuthn PRF key (plaintext meta; no secret). */
interface PrfKeyMeta { credentialId: string; salt: string; wrappedDek: string; createdAt: string }
const PRF_META = 'prfKeys';
interface StateMeta { failedAttempts: number; lastOnlineAuthAt: string; offlineWindowHours: number }

export type OfflineStatus = 'none' | 'locked' | 'unlocked';

export type UnlockResult =
  | { ok: true }
  | { ok: false; reason: 'wrong_pin'; attemptsLeft: number }
  | { ok: false; reason: 'wiped' }
  | { ok: false; reason: 'not_found' }
  | { ok: false; reason: 'biometric_failed' };

export interface EnrollInput {
  user: AuthUser;
  refreshToken: string;
  pin: string;
  deviceId: string;
  offlineSettings?: Partial<OfflineSettings>;
  /** Override calibration (tests). */
  iterations?: number;
}

interface StorageLike { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem(k: string): void }

const label = (user: AuthUser) => `${user.firstName || 'User'}${user.lastName ? ` ${user.lastName.charAt(0)}.` : ''}`;

function browserStorage(): StorageLike | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export class OfflineSessionImpl {
  private backendFactory: KvBackendFactory;
  private storage: StorageLike | null;
  private store: SecureStore | null = null;
  private identity: OfflineIdentity | null = null;
  private profile: OfflineProfile | null = null;
  private listeners = new Set<() => void>();

  constructor(backendFactory: KvBackendFactory = indexedDbBackend, storage: StorageLike | null = browserStorage()) {
    this.backendFactory = backendFactory;
    this.storage = storage;
  }

  get status(): OfflineStatus {
    if (this.store?.unlocked) return 'unlocked';
    return this.listIdentities().length > 0 ? 'locked' : 'none';
  }

  get currentIdentity(): OfflineIdentity | null { return this.identity; }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private notify() { this.listeners.forEach((l) => l()); }

  // ---- identity registry (plaintext, labels only) ----

  listIdentities(): OfflineIdentity[] {
    try {
      const raw = this.storage?.getItem(REGISTRY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((i) => i && typeof i.id === 'string') : [];
    } catch {
      return [];
    }
  }

  /** Most recently used identity first. */
  lastIdentity(): OfflineIdentity | null {
    return [...this.listIdentities()].sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt))[0] || null;
  }

  private saveRegistry(list: OfflineIdentity[]) {
    try {
      if (list.length) this.storage?.setItem(REGISTRY_KEY, JSON.stringify(list));
      else this.storage?.removeItem(REGISTRY_KEY);
    } catch {
      // storage unavailable: the lock screen falls back to password sign-in
    }
  }

  private upsertIdentity(identity: OfflineIdentity) {
    this.saveRegistry([...this.listIdentities().filter((i) => i.id !== identity.id), identity]);
  }

  private removeIdentity(id: string) {
    this.saveRegistry(this.listIdentities().filter((i) => i.id !== id));
  }

  async isEnrolled(tenantId: string, userId: string): Promise<boolean> {
    const id = await identityHash(tenantId, userId);
    return this.listIdentities().some((i) => i.id === id);
  }

  private async openStore(id: string): Promise<SecureStore> {
    return new SecureStore(await this.backendFactory(`${DB_PREFIX}${id}`));
  }

  // ---- lifecycle ----

  async enroll(input: EnrollInput): Promise<void> {
    const { user, refreshToken, pin, deviceId } = input;
    const id = await identityHash(user.tenantId, user.id);
    const store = await this.openStore(id);
    const salt = randomBytes(16);
    const iterations = input.iterations ?? await calibrateIterations();
    const kek = await deriveKek(pin, salt, iterations);
    const { wrapped, dek } = await createWrappedDek(kek);
    const now = new Date().toISOString();
    const offlineSettings = { ...DEFAULT_OFFLINE_SETTINGS, ...input.offlineSettings };

    await store.putMeta('keys', { wrappedDek: wrapped, salt: toBase64(salt), iterations } as KeyMeta);
    await store.putMeta('state', { failedAttempts: 0, lastOnlineAuthAt: now, offlineWindowHours: offlineSettings.offlineWindowHours } as StateMeta);
    store.setKey(dek);
    const profile: OfflineProfile = { user, deviceId, offlineSettings, enrolledAt: now, lastOnlineAuthAt: now };
    await store.put('profile', 'user', profile);
    await store.put('session', 'refresh', { refreshToken });

    this.store = store;
    this.profile = profile;
    this.identity = { id, label: label(user), lastUsedAt: now };
    this.upsertIdentity(this.identity);
    this.notify();
  }

  async unlock(identityId: string, pin: string): Promise<UnlockResult> {
    const known = this.listIdentities().find((i) => i.id === identityId);
    if (!known) return { ok: false, reason: 'not_found' };
    const store = await this.openStore(identityId);
    const keys = await store.getMeta<KeyMeta>('keys');
    const state = (await store.getMeta<StateMeta>('state')) || { failedAttempts: 0, lastOnlineAuthAt: '', offlineWindowHours: DEFAULT_OFFLINE_SETTINGS.offlineWindowHours };
    if (!keys) {
      this.removeIdentity(identityId);
      return { ok: false, reason: 'not_found' };
    }

    let dek: CryptoKey;
    try {
      dek = await unwrapDek(keys.wrappedDek, await deriveKek(pin, fromBase64(keys.salt), keys.iterations));
    } catch {
      const failedAttempts = state.failedAttempts + 1;
      if (failedAttempts >= MAX_PIN_ATTEMPTS) {
        await this.shred(identityId, store);
        return { ok: false, reason: 'wiped' };
      }
      await store.putMeta('state', { ...state, failedAttempts });
      return { ok: false, reason: 'wrong_pin', attemptsLeft: MAX_PIN_ATTEMPTS - failedAttempts };
    }

    return this.finishUnlock(known, store, dek, state);
  }

  private async finishUnlock(known: OfflineIdentity, store: SecureStore, dek: CryptoKey, state: StateMeta): Promise<UnlockResult> {
    store.setKey(dek);
    await store.putMeta('state', { ...state, failedAttempts: 0 });
    this.store = store;
    this.profile = (await store.get<OfflineProfile>('profile', 'user')) || null;
    this.identity = { ...known, lastUsedAt: new Date().toISOString() };
    this.upsertIdentity(this.identity);
    this.notify();
    return { ok: true };
  }

  // ---- 16D D2: biometric unlock (WebAuthn PRF) ----

  /** Plaintext check for the lock screen: this identity has a biometric key. */
  async biometricCredential(identityId: string): Promise<string | null> {
    const store = await this.openStore(identityId);
    return (await store.getMeta<PrfKeyMeta>(PRF_META))?.credentialId || null;
  }

  /**
   * Add the second wrapped DEK. Needs the PIN (the in-memory DEK is non-extractable, so the PIN
   * unwraps an extractable copy only for this re-wrap). Throws on a wrong PIN.
   */
  async addBiometric(pin: string, credentialId: string, secret: ArrayBuffer): Promise<void> {
    if (!this.store?.unlocked) throw new Error('Offline store is locked');
    const keys = await this.store.getMeta<KeyMeta>('keys');
    if (!keys) throw new Error('Offline mode is not set up');
    let extractable: CryptoKey;
    try {
      extractable = await unwrapDekForRewrap(keys.wrappedDek, await deriveKek(pin, fromBase64(keys.salt), keys.iterations));
    } catch {
      throw new Error('Wrong PIN');
    }
    const salt = randomBytes(16);
    const kek = await deriveKekFromSecret(secret, salt, PRF_HKDF_INFO);
    const meta: PrfKeyMeta = { credentialId, salt: toBase64(salt), wrappedDek: await wrapDek(extractable, kek), createdAt: new Date().toISOString() };
    await this.store.putMeta(PRF_META, meta);
    this.notify();
  }

  async removeBiometric(identityId: string | undefined = this.identity?.id): Promise<void> {
    if (!identityId) return;
    const store = this.identity?.id === identityId && this.store ? this.store : await this.openStore(identityId);
    await store.deleteMeta(PRF_META);
    this.notify();
  }

  /** Unlock with the PRF secret from `evaluate(credentialId)`. A failure never counts as a wrong PIN. */
  async unlockWithBiometric(identityId: string, evaluate: (credentialId: string) => Promise<ArrayBuffer>): Promise<UnlockResult> {
    const known = this.listIdentities().find((i) => i.id === identityId);
    if (!known) return { ok: false, reason: 'not_found' };
    const store = await this.openStore(identityId);
    const prf = await store.getMeta<PrfKeyMeta>(PRF_META);
    const state = (await store.getMeta<StateMeta>('state')) || { failedAttempts: 0, lastOnlineAuthAt: '', offlineWindowHours: DEFAULT_OFFLINE_SETTINGS.offlineWindowHours };
    if (!prf || !(await store.getMeta<KeyMeta>('keys'))) return { ok: false, reason: 'biometric_failed' };
    let dek: CryptoKey;
    try {
      const secret = await evaluate(prf.credentialId);
      dek = await unwrapDek(prf.wrappedDek, await deriveKekFromSecret(secret, fromBase64(prf.salt), PRF_HKDF_INFO));
    } catch {
      return { ok: false, reason: 'biometric_failed' };
    }
    return this.finishUnlock(known, store, dek, state);
  }

  /** Drop the key from memory. Safe to call when already locked. */
  lock(): void {
    this.store?.clearKey();
    this.store = null;
    this.profile = null;
    this.notify();
  }

  /** Delete an identity's data from this device (explicit sign-out, revoked device). */
  async wipe(identityId: string | undefined = this.identity?.id): Promise<void> {
    if (!identityId) return;
    const store = this.identity?.id === identityId && this.store ? this.store : await this.openStore(identityId);
    await store.destroy();
    this.removeIdentity(identityId);
    if (this.identity?.id === identityId) {
      this.store = null;
      this.profile = null;
      this.identity = null;
    }
    this.notify();
  }

  /**
   * Too many wrong PINs: drop the wrapped key and every readable store. Sealed escrow (16C) is
   * kept so documented care can still reach the server; with no escrow the database is deleted.
   */
  private async shred(identityId: string, store: SecureStore): Promise<void> {
    await store.deleteMeta('keys');
    await store.deleteMeta(PRF_META); // the biometric copy of the DEK goes too
    for (const name of ['profile', 'session', 'packets', 'visits', 'chartEntries', 'assessments', 'outbox', 'blobs', 'drafts'] as const) {
      await store.clear(name);
    }
    if (!(await store.hasRows('escrow'))) await store.destroy();
    this.removeIdentity(identityId);
    this.notify();
  }

  // ---- 16C lifecycle edges (plan C12) ----

  /** Unsynced op count of an enrolled identity, readable while locked (R-311 prompt). */
  async lockedPendingCount(tenantId: string, userId: string): Promise<{ identityId: string; count: number } | null> {
    const id = await identityHash(tenantId, userId);
    if (!this.listIdentities().some((i) => i.id === id)) return null;
    const store = this.identity?.id === id && this.store ? this.store : await this.openStore(id);
    return { identityId: id, count: await store.countRows('outbox') };
  }

  /**
   * Explicit sign-out with unsynced work (design §6.3): keep the outbox, blobs, local records and
   * escrow encrypted on this device, drop the patient packets, and lock.
   */
  async signOutKeepingQueue(): Promise<void> {
    if (this.store?.unlocked) await this.store.clear('packets');
    this.lock();
  }

  /**
   * Sealed copies left after a PIN shred (identity gone from the registry, database kept because
   * escrow had rows). The same user uploads them after an online sign-in.
   */
  async orphanEscrow(tenantId: string, userId: string): Promise<{ identityId: string; store: SecureStore } | null> {
    const id = await identityHash(tenantId, userId);
    if (this.listIdentities().some((i) => i.id === id)) return null;
    const store = await this.openStore(id);
    if (!(await store.hasRows('escrow'))) {
      if (!(await store.getMeta('keys'))) await store.destroy().catch(() => undefined);
      return null;
    }
    return { identityId: id, store };
  }

  // ---- unlocked accessors ----

  getProfile(): OfflineProfile | null { return this.profile; }

  /** The unlocked store, or null when locked. */
  getStore(): SecureStore | null { return this.store?.unlocked ? this.store : null; }

  async getRefreshToken(): Promise<string | null> {
    const row = await this.store?.get<{ refreshToken: string }>('session', 'refresh');
    return row?.refreshToken || null;
  }

  async saveRefreshToken(refreshToken: string): Promise<void> {
    if (!this.store?.unlocked) return;
    await this.store.put('session', 'refresh', { refreshToken });
  }

  /** After a successful online sign-in or refresh: restart the offline window, refresh the snapshot. */
  async markOnlineAuth(user?: AuthUser, offlineSettings?: Partial<OfflineSettings>): Promise<void> {
    if (!this.store?.unlocked || !this.profile) return;
    const now = new Date().toISOString();
    const settings = { ...this.profile.offlineSettings, ...offlineSettings };
    this.profile = { ...this.profile, user: user || this.profile.user, offlineSettings: settings, lastOnlineAuthAt: now };
    await this.store.put('profile', 'user', this.profile);
    const state = await this.store.getMeta<StateMeta>('state');
    await this.store.putMeta('state', { failedAttempts: state?.failedAttempts ?? 0, lastOnlineAuthAt: now, offlineWindowHours: settings.offlineWindowHours });
    this.notify();
  }

  /** Offline use is allowed within the window after the last online sign-in (encrypted value). */
  offlineAccessAllowed(nowMs: number = Date.now()): boolean {
    if (!this.profile) return false;
    const hours = Math.min(24, this.profile.offlineSettings.offlineWindowHours || DEFAULT_OFFLINE_SETTINGS.offlineWindowHours);
    return nowMs - Date.parse(this.profile.lastOnlineAuthAt) <= hours * 3600 * 1000;
  }

  /** Lock-screen hint before unlock (plaintext copy; the encrypted check above is authoritative). */
  async offlineWindowHint(identityId: string, nowMs: number = Date.now()): Promise<{ expired: boolean; lastOnlineAuthAt: string | null }> {
    const store = await this.openStore(identityId);
    const state = await store.getMeta<StateMeta>('state');
    if (!state?.lastOnlineAuthAt) return { expired: true, lastOnlineAuthAt: null };
    const expired = nowMs - Date.parse(state.lastOnlineAuthAt) > Math.min(24, state.offlineWindowHours) * 3600 * 1000;
    return { expired, lastOnlineAuthAt: state.lastOnlineAuthAt };
  }
}

/** App-wide offline session. */
export const OfflineSession = new OfflineSessionImpl();

export default OfflineSession;
